import { Alert, Form, Input, Tabs } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { WorkflowTemplateSettingsSection } from './WorkflowTemplateSettingsSection';
import { WorkflowTemplateStepsSection } from './WorkflowTemplateStepsSection';
import type {
  Option,
  WorkflowActionSchema,
  WorkflowDecisionSchema,
} from './WorkflowTemplateBuilder.types';

type WorkflowTemplateBuilderProps = {
  workflowActionSchemas: Record<string, WorkflowActionSchema>;
  workflowDecisionSchemas: Record<string, WorkflowDecisionSchema>;
  conditionOptions: Option[];
};

export const WorkflowTemplateBuilder = ({
  workflowActionSchemas,
  workflowDecisionSchemas,
  conditionOptions,
}: WorkflowTemplateBuilderProps) => {
  const form = Form.useFormInstance();
  const workflowValue = Form.useWatch(['workflow'], form);
  const [activeTab, setActiveTab] = useState<'graph' | 'json'>('graph');
  const [jsonDraft, setJsonDraft] = useState('{}');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const normalizedWorkflowValue = useMemo(
    () => workflowValue || {},
    [workflowValue]
  );

  useEffect(() => {
    if (activeTab !== 'json') return;
    setJsonDraft(JSON.stringify(normalizedWorkflowValue, null, 2));
  }, [activeTab, normalizedWorkflowValue]);

  const handleJsonChange = (raw: string) => {
    setJsonDraft(raw);
    try {
      const parsed = JSON.parse(raw);
      form.setFieldValue(['workflow'], parsed);
      setJsonError(null);
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  return (
    <div className="space-y-4">
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as 'graph' | 'json')}
        items={[
          {
            key: 'graph',
            label: 'Graph',
            children: (
              <div className="space-y-4">
                <WorkflowTemplateSettingsSection />
                <WorkflowTemplateStepsSection
                  workflowActionSchemas={workflowActionSchemas}
                  workflowDecisionSchemas={workflowDecisionSchemas}
                  conditionOptions={conditionOptions}
                />
              </div>
            ),
          },
          {
            key: 'json',
            label: 'JSON',
            children: (
              <div className="space-y-3">
                {jsonError ? (
                  <Alert
                    type="error"
                    showIcon
                    message={`Invalid JSON: ${jsonError}`}
                  />
                ) : null}
                <Input.TextArea
                  className="font-mono"
                  rows={24}
                  value={jsonDraft}
                  onChange={(event) => handleJsonChange(event.target.value)}
                  placeholder="Paste workflow JSON"
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export type { WorkflowTemplateBuilderValue } from './WorkflowTemplateBuilder.types';
